import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { InstalledUpiApp } from '../../../../utils/installedUpiIntents'
import { getBottomPadding, getTopPadding } from '../../../../utils/sessionStorage'
import { upiIcon } from '../../../../utils/mf-execution/paymentPageConfig'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import BackButton from '../../../../components/ui-components/BackButton'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { AutoPayBankAccount, AutoPayMandateDetails, AutopayMethodSelection, AutopayPaymentMode, InvestPaymentMethodType } from '../../../../types/mf-execution.types'
import { convertNumberToCurrency, roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { SelectBankAccountBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/SelectBankAccountBottomSheet'
import { getUpiAppSelectionId } from '../../../../utils/mf-execution/paymentMethodSelection'
import {
  buildAutopayMethodSelectionFromNetBanking,
  buildAutopayMethodSelectionFromUpi,
  bankAccountToPaymentMethodRow,
  getAutopayNetBankingSelectionId,
  getDisabledAutopayUpiPlaceholderApps,
  getSelectionIdFromAutopayMethod,
  hasActiveNetBankingMandateForAccount,
  isAmountWithinUpiAutopayLimit,
  isAutopayConfirmCtaEnabled,
  findMandateAccountForAutopayMode,
  filterInstalledUpiAppsForAutopay,
  mandateAccountToPaymentMethodRow,
  paymentMethodRowToMandateAccount,
  resolveConfirmableAutopayMethod,
} from '../../../../utils/mf-execution/autopayMethodSelection'
import { getBankLogoSrc } from '../../../../utils/mf-execution/paymentMethodsScreenUtils'
import { PaymentMethodExpandableSection } from '../../../../components/execution/payment-methods/PaymentMethodExpandableSection'
import { PaymentMethodItem } from '../../../../components/execution/payment-methods/PaymentMethodItem'
import { PaymentMethodSelectableRow } from '../../../../components/execution/payment-methods/PaymentMethodSelectableRow'
import { UpiLinkedBankDetails } from '../../../../components/execution/payment-methods/UpiLinkedBankDetails'
import { trackEvent } from '../../../../utils/appBridge'
import { EventNames } from '../../../../types/misc.types'

const SCREEN_NAME = 'Select autopay method'

export type AutopayMethodsScreenProps = {
  amount: number
  upiAutoPayMaxLimit?: number
  requiredPaymentMode?: AutopayPaymentMode
  /** `availableMandates` — mandate id/type lookup for setup API and "Active" badge. */
  mandateAccounts: AutoPayMandateDetails[]
  /** `bankAccounts` from mandate-details — net-banking rows and UPI linked-bank list. */
  bankAccounts: AutoPayBankAccount[]
  selectedAutopayMethod: AutopayMethodSelection | null
  onAutopayMethodChange: (selection: AutopayMethodSelection) => void
  installedUpiApps: InstalledUpiApp[] | null
  requestUpiIntents: () => void
  onConfirm?: (selection: AutopayMethodSelection) => void
}

/**
 * Full-screen autopay method picker for the v2 Setup Autopay flow.
 * Layout mirrors `PaymentMethodsScreen` (UPI apps, net banking, Confirm CTA). Net banking rows
 * use `bankAccounts`; UPI mandate resolution uses `availableMandates`. Confirm is handled by the route
 * wrapper so navigation back preserves query params and passes selection via `location.state`.
 */
export function AutopayMethodsScreen({
  amount,
  upiAutoPayMaxLimit = 0,
  requiredPaymentMode,
  mandateAccounts,
  bankAccounts,
  selectedAutopayMethod,
  onAutopayMethodChange,
  installedUpiApps,
  requestUpiIntents,
  onConfirm,
}: AutopayMethodsScreenProps) {
  const navigate = useNavigate()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const eligibleInstalledUpiApps = useMemo(
    () => filterInstalledUpiAppsForAutopay(installedUpiApps),
    [installedUpiApps]
  )
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() =>
    getSelectionIdFromAutopayMethod(selectedAutopayMethod, eligibleInstalledUpiApps)
  )
  const [selectedBankAccount, setSelectedBankAccount] = useState<InvestPaymentMethodType | null>(() => {
    const account = bankAccounts.find((item) => item?.accountId === selectedAutopayMethod?.accountId)
    return account ? bankAccountToPaymentMethodRow(account) : null
  })
  const [pendingSelection, setPendingSelection] = useState<AutopayMethodSelection | null>(
    () => selectedAutopayMethod
  )
  const hasAutoSelectedDefaultUpiRef = useRef(false)

  const bankAccountRows = bankAccounts.map(bankAccountToPaymentMethodRow)
  const hasInstalledUpiIntents = eligibleInstalledUpiApps.length > 0

  const isUpiAutopayOverLimit =
    amount > 0 &&
    (upiAutoPayMaxLimit ?? 0) > 0 &&
    !isAmountWithinUpiAutopayLimit(amount, upiAutoPayMaxLimit)

  const isNetBankingRequired = requiredPaymentMode === 'NET_BANKING'
  const isNetBankingFirstLayout = isUpiAutopayOverLimit || isNetBankingRequired
  const canSelectUpi = hasInstalledUpiIntents && !isUpiAutopayOverLimit && !isNetBankingRequired
  const isUpiOptionDisabled = isUpiAutopayOverLimit || isNetBankingRequired
  const shouldShowDisabledUpiPlaceholders = isNetBankingRequired && !hasInstalledUpiIntents
  const shouldShowUpiSection =
    canSelectUpi || (hasInstalledUpiIntents && isUpiOptionDisabled) || shouldShowDisabledUpiPlaceholders

  const upiAppsToDisplay = hasInstalledUpiIntents
    ? eligibleInstalledUpiApps
    : shouldShowDisabledUpiPlaceholders
      ? getDisabledAutopayUpiPlaceholderApps()
      : []

  const upiLimitLabel =
    (upiAutoPayMaxLimit ?? 0) > 0
      ? convertNumberToCurrency({ amount: upiAutoPayMaxLimit, isRound: true, casing: 'PASCAL' })
      : ''

  useEffect(() => {
    requestUpiIntents()
  }, [requestUpiIntents])

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: SCREEN_NAME, amount }
    })
  }, [])

  const installedUpiAppNames = (installedUpiApps ?? []).map((app) => app?.name ?? '')
  const netBankingAccountNumbers = bankAccounts.map((account) => account?.displayAccNumber ?? '')

  // Keyed on joined names: fires on open, and again only if the native UPI refresh changes the list
  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: SCREEN_NAME,
        eventId: 'MFE_SELECT_AUTOPAY_METHOD_AVAILABLE_METHODS',
        amount,
        upiApps: installedUpiAppNames,
        netBankingAccounts: netBankingAccountNumbers
      }
    })
  }, [installedUpiAppNames.join(','), netBankingAccountNumbers.join(',')])

  const trackAutopayTap = (
    eventName: EventNames,
    eventId: string,
    buttonLabel: string,
    autopayMode?: AutopayPaymentMode
  ) => {
    trackEvent({
      eventName,
      payload: {
        ScreenName: SCREEN_NAME,
        eventId,
        buttonLabel,
        amount,
        ...(autopayMode ? { autopayMode } : {})
      }
    })
  }

  useEffect(() => {
    if (!selectedAutopayMethod?.accountId) return

    setPendingSelection(selectedAutopayMethod)
    setSelectedOptionId(getSelectionIdFromAutopayMethod(selectedAutopayMethod, eligibleInstalledUpiApps))

    const account = bankAccounts.find((item) => item?.accountId === selectedAutopayMethod.accountId)
    if (account) {
      setSelectedBankAccount(bankAccountToPaymentMethodRow(account))
    }
  }, [selectedAutopayMethod, eligibleInstalledUpiApps, bankAccounts])

  const updatePendingSelection = (selection: AutopayMethodSelection | null) => {
    if (!selection?.accountId) return
    setPendingSelection(selection)
  }

  const handleBankAccountConfirm = (bankAccount: InvestPaymentMethodType) => {
    trackAutopayTap(
      EventNames.UserClicked,
      'MFE_SELECT_AUTOPAY_METHOD_BANK_CHANGE',
      bankAccount?.displayAccNumber ?? '',
      'UPI'
    )
    setSelectedBankAccount(bankAccount)

    const mandateAccount = paymentMethodRowToMandateAccount(bankAccount, mandateAccounts, 'UPI', bankAccounts)
    if (!mandateAccount) return

    if (!selectedOptionId?.startsWith('upi:')) return

    const selectedApp = eligibleInstalledUpiApps.find((app) => getUpiAppSelectionId(app) === selectedOptionId)
    if (!selectedApp) return

    updatePendingSelection(buildAutopayMethodSelectionFromUpi(selectedApp, mandateAccount))
  }

  const openSelectBankAccountBottomSheet = () => {
    if (bankAccountRows.length <= 1) return

    trackAutopayTap(
      EventNames.UserClicked,
      'MFE_SELECT_AUTOPAY_METHOD_SELECT_BANK',
      selectedBankAccount?.displayAccNumber ?? '',
      'UPI'
    )
    openBottomSheet(
      <SelectBankAccountBottomSheet
        ScreenName='AutopayMethodsScreen'
        bankAccounts={bankAccountRows}
        selectedBankAccount={selectedBankAccount}
        onDismiss={closeBottomSheet}
        onConfirm={handleBankAccountConfirm}
      />
    )
  }

  const syncUpiIntentSelection = (app: InstalledUpiApp) => {
    const selectionId = getUpiAppSelectionId(app)
    const mandateAccount =
      paymentMethodRowToMandateAccount(selectedBankAccount, mandateAccounts, 'UPI', bankAccounts) ??
      (bankAccounts[0]
        ? findMandateAccountForAutopayMode(mandateAccounts, bankAccounts[0].accountId, 'UPI', bankAccounts)
        : null)

    if (!mandateAccount?.accountId) return

    if (!selectedBankAccount) {
      setSelectedBankAccount(mandateAccountToPaymentMethodRow(mandateAccount))
    }

    setSelectedOptionId(selectionId)
    updatePendingSelection(buildAutopayMethodSelectionFromUpi(app, mandateAccount))
  }

  const handleUpiAppSelect = (app: InstalledUpiApp) => {
    if (isUpiOptionDisabled) return
    trackAutopayTap(EventNames.UserClicked, 'MFE_SELECT_AUTOPAY_METHOD_UPI_APP', app?.name ?? '', 'UPI')
    syncUpiIntentSelection(app)
  }

  const handleNetBankingSelect = (account: AutoPayBankAccount) => {
    const mandateAccount = findMandateAccountForAutopayMode(
      mandateAccounts,
      account.accountId,
      'NET_BANKING',
      bankAccounts
    )
    if (!mandateAccount) return

    trackAutopayTap(
      EventNames.UserClicked,
      'MFE_SELECT_AUTOPAY_METHOD_NET_BANKING',
      account?.displayAccNumber ?? '',
      'NET_BANKING'
    )
    const selectionId = getAutopayNetBankingSelectionId(account.accountId)
    setSelectedOptionId(selectionId)
    setSelectedBankAccount(bankAccountToPaymentMethodRow(account))
    updatePendingSelection(buildAutopayMethodSelectionFromNetBanking(mandateAccount))
  }

  const confirmResolutionParams = {
    pendingSelection,
    selectedOptionId,
    selectedBankAccount,
    mandateAccounts,
    bankAccounts,
    installedUpiApps: eligibleInstalledUpiApps,
  }

  const handleConfirmClick = () => {
    const selection = resolveConfirmableAutopayMethod(confirmResolutionParams)
    if (!selection?.accountId) return

    trackAutopayTap(EventNames.ButtonTap, 'MFE_SELECT_AUTOPAY_METHOD_CTA_CONFIRM', 'Confirm', selection.mode)
    // Change = confirmed method differs from the one SetupAutoPay passed in (not every row tap)
    if (
      selection.mode !== selectedAutopayMethod?.mode ||
      selection.accountId !== selectedAutopayMethod?.accountId ||
      selection.upiPackageName !== selectedAutopayMethod?.upiPackageName
    ) {
      trackAutopayTap(
        EventNames.UserClicked,
        'MFE_SELECT_AUTOPAY_METHOD_CHANGED',
        (selection.mode === 'UPI' ? selection.upiAppName : selection.displayAccNumber) ?? '',
        selection.mode
      )
    }

    onAutopayMethodChange(selection)
    onConfirm?.(selection)
  }

  useEffect(() => {
    if (canSelectUpi) return
    if (!bankAccounts.length) return

    const firstBankAccount = bankAccounts[0]
    const mandateAccount = findMandateAccountForAutopayMode(
      mandateAccounts,
      firstBankAccount.accountId,
      'NET_BANKING',
      bankAccounts
    )
    if (!mandateAccount) return

    const netBankingSelectionId = getAutopayNetBankingSelectionId(firstBankAccount.accountId)

    if (
      selectedOptionId === netBankingSelectionId &&
      pendingSelection?.mode === 'NET_BANKING' &&
      pendingSelection?.accountId === firstBankAccount.accountId
    ) {
      return
    }

    setSelectedOptionId(netBankingSelectionId)
    setSelectedBankAccount(bankAccountToPaymentMethodRow(firstBankAccount))
    updatePendingSelection(buildAutopayMethodSelectionFromNetBanking(mandateAccount))
  }, [
    canSelectUpi,
    bankAccounts,
    mandateAccounts,
    selectedOptionId,
    pendingSelection?.mode,
    pendingSelection?.accountId,
  ])

  useEffect(() => {
    if (!canSelectUpi) return
    if (!selectedOptionId?.startsWith('upi:') || !eligibleInstalledUpiApps.length) return

    const selectedApp = eligibleInstalledUpiApps.find((app) => getUpiAppSelectionId(app) === selectedOptionId)
    const mandateAccount =
      paymentMethodRowToMandateAccount(selectedBankAccount, mandateAccounts, 'UPI', bankAccounts) ??
      (bankAccounts[0]
        ? findMandateAccountForAutopayMode(mandateAccounts, bankAccounts[0].accountId, 'UPI', bankAccounts)
        : null)

    if (!selectedApp || !mandateAccount?.accountId) return

    const nextSelection = buildAutopayMethodSelectionFromUpi(selectedApp, mandateAccount)
    if (!nextSelection) return

    const needsSync =
      pendingSelection?.mode !== 'UPI' ||
      pendingSelection?.upiPackageName !== nextSelection.upiPackageName ||
      pendingSelection?.accountId !== nextSelection.accountId

    if (needsSync) {
      updatePendingSelection(nextSelection)
    }
  }, [
    canSelectUpi,
    selectedOptionId,
    eligibleInstalledUpiApps,
    selectedBankAccount,
    mandateAccounts,
    bankAccounts,
    pendingSelection?.mode,
    pendingSelection?.upiPackageName,
    pendingSelection?.accountId,
  ])

  useEffect(() => {
    if (!canSelectUpi) return
    if (hasAutoSelectedDefaultUpiRef.current || !eligibleInstalledUpiApps.length || !bankAccounts.length) return
    if (pendingSelection?.upiPackageName || selectedOptionId) return
    if (pendingSelection?.mode === 'NET_BANKING') return

    const defaultApp = eligibleInstalledUpiApps[0]
    if (!defaultApp) return

    hasAutoSelectedDefaultUpiRef.current = true
    syncUpiIntentSelection(defaultApp)
  }, [
    canSelectUpi,
    eligibleInstalledUpiApps,
    mandateAccounts,
    selectedOptionId,
    pendingSelection?.mode,
    pendingSelection?.upiPackageName,
  ])

  const isConfirmDisabled = !isAutopayConfirmCtaEnabled(confirmResolutionParams)

  const netBankingSection = (
    <div className={isNetBankingFirstLayout ? '' : 'pt-5'}>
      <p className='text-sm text-grey40 justify-start px-5 mb-4'>Setup via Net Banking</p>
      <div>
        {bankAccounts.map((account, index, accounts) => (
          <PaymentMethodSelectableRow
            key={`${account?.accountId ?? account?.displayAccNumber ?? index}`}
            showBorderBelow={index < accounts.length - 1}
          >
            <PaymentMethodItem
              imageSrc={getBankLogoSrc(account?.bankUrlLogo)}
              imageAlt={account?.displayAccNumber ?? 'Net banking account'}
              title={account?.displayAccNumber ?? ''}
              imageSizeClassName='w-10 h-10'
              isSelected={selectedOptionId === getAutopayNetBankingSelectionId(account.accountId)}
              subtitle={
                hasActiveNetBankingMandateForAccount(account.accountId, mandateAccounts) ? (
                  <AutopayBankAccountActiveStatus />
                ) : undefined
              }
              onSelect={() => handleNetBankingSelect(account)}
            />
          </PaymentMethodSelectableRow>
        ))}
      </div>
    </div>
  )

  const upiLimitCaption = isNetBankingRequired && !isUpiAutopayOverLimit
    ? `Maximum ${roundOffAndFormatNumberWithCurrency(15000)} limit allowed`
    : `UPI Autopay is limited to ${upiLimitLabel}`

  const upiSection = (
    <>
      <div className='flex flex-row items-center gap-2 w-full px-5 mt-4'>
        <p className='text-sm text-grey40 justify-start'>Setup via</p>
        <RenderHTML innerHTMLText={upiIcon} />
      </div>
      {isUpiOptionDisabled && upiLimitLabel ? (
        <p className='text-[14px] font-[400] text-[#D9892B] px-5 mb-5'>{upiLimitCaption}</p>
      ) : null}
      <ul className='flex flex-col'>
        {upiAppsToDisplay.map((app, index, apps) => {
          const isUpiSelected = canSelectUpi && selectedOptionId === getUpiAppSelectionId(app)

          return (
            <li
              key={`${app.packageName ?? app.name}-${index}`}
              className={index < apps.length - 1 ? 'border-b border-grey60' : ''}
            >
              <PaymentMethodSelectableRow>
                <PaymentMethodItem
                  imageSrc={app?.appLogoUrl}
                  imageAlt={app?.name ?? ''}
                  title={app?.name ?? ''}
                  imageSizeClassName='w-10 h-10'
                  isSelected={isUpiSelected}
                  isDisabled={isUpiOptionDisabled}
                  onSelect={() => handleUpiAppSelect(app)}
                />
                <PaymentMethodExpandableSection isExpanded={isUpiSelected && bankAccountRows.length > 0}>
                  <UpiLinkedBankDetails
                    bankAccounts={bankAccountRows}
                    selectedBankAccount={selectedBankAccount}
                    onSelectBankClick={bankAccountRows.length > 1 ? openSelectBankAccountBottomSheet : undefined}
                    isAutopay
                  />
                </PaymentMethodExpandableSection>
              </PaymentMethodSelectableRow>
            </li>
          )
        })}
      </ul>
    </>
  )

  return (
    <div
      className='h-screen overflow-y-auto no-scrollbar overscroll-none bg-white'
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
    >
      <div
        className='sticky top-0 z-30 bg-white px-5 pb-4'
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
      >
        <div className='flex items-center justify-between w-full'>
          <BackButton size={24} onClick={() => navigate(-1)} />
          <div className='text-[16px] font-[500] text-[#232323]'>Select autopay method</div>
          <div className='w-9' />
        </div>
      </div>
      <div className='pb-24'>
        {isNetBankingFirstLayout ? (
          <>
            {netBankingSection}
            {shouldShowUpiSection ? (
              <>
                <div className='bg-grey80 h-4' />
                {upiSection}
              </>
            ) : null}
          </>
        ) : (
          <>
            {shouldShowUpiSection ? upiSection : null}
            {shouldShowUpiSection && bankAccounts.length > 0 ? <div className='bg-grey80 h-4' /> : null}
            {netBankingSection}
          </>
        )}
      </div>
      <div
        className='absolute bottom-0 left-0 right-0 z-20 bg-white p-5'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      >
        <PrimaryButton onClick={handleConfirmClick} label='Confirm' isDisabled={isConfirmDisabled} />
      </div>
    </div>
  )
}

const autopayActiveStatusIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.78477 0.0209769C4.93494 0.0724772 5.03528 0.209728 5.03528 0.363654V3.18182H7.62351C7.76875 3.18182 7.90104 3.26251 7.96346 3.38917C8.02588 3.51584 8.00728 3.66588 7.91572 3.77476L3.63335 8.86564C3.53286 8.98513 3.36538 9.03052 3.21521 8.97902C3.06504 8.92753 2.9647 8.79023 2.9647 8.63634V5.81817H0.376473C0.231241 5.81817 0.0989611 5.73748 0.0365449 5.61082C-0.0258716 5.48415 -0.0072919 5.33411 0.0842901 5.22523L4.36663 0.134349C4.46712 0.0148851 4.6346 -0.0305234 4.78477 0.0209769Z" fill="#2A9C4A"/>
</svg>`

/** Shown under a net-banking bank row when `availableMandates` has a net-banking mandate for that account. */
export function AutopayBankAccountActiveStatus() {
  return (
    <div className='flex items-center gap-1 mt-0.5'>
      <RenderHTML innerHTMLText={autopayActiveStatusIcon} />
      <span className='text-13 font-medium text-[#2A9C4A]'>Active</span>
    </div>
  )
}
